import { FC, useState } from "react";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import "./qrCodeCard.css";

interface QrCodeCardProps {
  qrCode: {
    _id: string;
    image: string;
    title: string;
    shortenedUrl: string;
    fullUrl: string;
    totalVisits: number;
    createdDate: string;
    description?: string;
  };
  onDelete: (id: string, imageUrl: string) => void;
}

const QrCodeCard: FC<QrCodeCardProps> = ({ qrCode, onDelete }) => {
  const navigate = useNavigate();
  const { user } = UserAuth();
  const [showModal, setShowModal] = useState(false);

  const downloadQrCode = async (id: string, format: string) => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/qrcodes/${id}/image?format=${format}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download QR code");
      }

      const blob = await response.blob();
      saveAs(blob, `qrsharp.${format}`);
      setShowModal(false);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Format date
  const formattedDate = new Date(qrCode.createdDate).toLocaleDateString();

  return (
    <>
      <div className="qr-code-card">
        <div className="qr-code-card__image">
          <img src={qrCode.image} alt={`QR Code for ${qrCode.title}`} />
        </div>

        <div className="qr-code-card__content">
          {/* Mobile actions - visible only on small screens */}
          <div className="qr-code-card__mobile-actions">
            <button
              className="qr-code-card__action-btn download"
              onClick={() => setShowModal(true)}
              title="Download QR Code"
            >
              <i className="fas fa-download"></i>
            </button>
            <button
              className="qr-code-card__action-btn edit"
              onClick={() => navigate(`edit/${qrCode._id}`)}
              title="Edit QR Code"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="qr-code-card__action-btn delete"
              onClick={() => onDelete(qrCode._id, qrCode.image)}
              title="Delete QR Code"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>

          <div className="qr-code-card__header">
            <h3>{qrCode.title}</h3>
            {/* Desktop actions - hidden on small screens */}
            <div className="qr-code-card__desktop-actions">
              <button
                className="qr-code-card__action-btn download"
                onClick={() => setShowModal(true)}
                title="Download QR Code"
              >
                <i className="fas fa-download"></i>
              </button>
              <button
                className="qr-code-card__action-btn edit"
                onClick={() => navigate(`edit/${qrCode._id}`)}
                title="Edit QR Code"
              >
                <i className="fas fa-edit"></i>
              </button>
              <button
                className="qr-code-card__action-btn delete"
                onClick={() => onDelete(qrCode._id, qrCode.image)}
                title="Delete QR Code"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>

          <p className="qr-code-card__description">
            {qrCode.description || "No description available"}
          </p>

          <a
            href={qrCode.fullUrl}
            className="qr-code-card__url"
            target="_blank"
            rel="noopener noreferrer"
          >
            {qrCode.fullUrl}
          </a>

          <div className="qr-code-card__meta">
            <div className="qr-code-card__meta-item">
              <i className="far fa-calendar-alt"></i>
              <span>{formattedDate}</span>
            </div>
            <div className="qr-code-card__meta-item">
              <i className="fas fa-chart-line"></i>
              <span>{qrCode.totalVisits} scans</span>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Download QR Code</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select your preferred file format:</p>
          <div className="download-options">
            <Button
              variant="primary"
              onClick={() => downloadQrCode(qrCode._id, "png")}
            >
              PNG
            </Button>
            <Button
              variant="primary"
              onClick={() => downloadQrCode(qrCode._id, "jpeg")}
            >
              JPEG
            </Button>
            <Button
              variant="primary"
              onClick={() => downloadQrCode(qrCode._id, "svg")}
            >
              SVG
            </Button>
            <Button
              variant="primary"
              onClick={() => downloadQrCode(qrCode._id, "webp")}
            >
              WEBP
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default QrCodeCard;
